import {
  keepPreviousData,
  type QueryClient,
  queryOptions,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { ApiError, api, baseHeaders, buildUrl, type ListResponse, toApiError } from "@/api/client"
import type { CancelResult } from "@/api/generated/CancelResult"
import type { RoomAttachment } from "@/api/generated/RoomAttachment"
import type { RoomContextBullet } from "@/api/generated/RoomContextBullet"
import type { RoomDetail } from "@/api/generated/RoomDetail"
import type { RoomFile } from "@/api/generated/RoomFile"
import type { RoomMessage } from "@/api/generated/RoomMessage"
import type { RoomSearchResult } from "@/api/generated/RoomSearchResult"
import type { RoomSummary } from "@/api/generated/RoomSummary"
import type { RoomWorker } from "@/api/generated/RoomWorker"
import type { StepAccepted } from "@/api/generated/StepAccepted"
import type { Team } from "@/api/generated/Team"
import { useApiMutation } from "@/api/mutation"

/** Run phases reported by the server (`rooms.run_phase`). */
export const ROOM_PHASES = [
  "agent_running",
  "awaiting_user_input",
  "idle_active",
  "turn_budget_exhausted",
  "paused",
] as const
export type RoomPhase = (typeof ROOM_PHASES)[number]

export interface RoomFilters {
  q?: string
  team_id?: number
  /** Comma-separated run phases. */
  run_phase?: string
  /** Only rooms started by this team automation. */
  team_automation_id?: number
}

export type SearchScope = "all" | "data" | "files"

export const roomKeys = {
  all: ["rooms"] as const,
  lists: () => [...roomKeys.all, "list"] as const,
  list: (f: RoomFilters) => [...roomKeys.lists(), f] as const,
  detail: (id: number) => [...roomKeys.all, "detail", id] as const,
  messages: (id: number) => [...roomKeys.all, "messages", id] as const,
  files: (id: number) => [...roomKeys.all, "files", id] as const,
  file: (id: number, fileId: number) => [...roomKeys.all, "file", id, fileId] as const,
  search: (id: number, q: string, scope: SearchScope) => [...roomKeys.all, "search", id, q, scope] as const,
  /** Team picker for "new room" (team CRUD lives in Studio → Teams). */
  teamPicker: () => [...roomKeys.all, "team-picker"] as const,
}

// ------------------------------------------------------------------ rooms

export const roomsQuery = (f: RoomFilters = {}) =>
  queryOptions({
    queryKey: roomKeys.list(f),
    queryFn: ({ signal }) =>
      api.get<ListResponse<RoomSummary>>("/rooms", { signal, query: { ...f, q: f.q?.trim(), limit: 500 } }),
    placeholderData: keepPreviousData,
    // follow rooms that are running elsewhere until they settle
    refetchInterval: (query) =>
      query.state.data?.items.some((r) => r.run_phase === "agent_running") ? 5000 : 30_000,
  })

export const useRooms = (f: RoomFilters = {}) => useQuery(roomsQuery(f))

export const roomQuery = (id: number) =>
  queryOptions({
    queryKey: roomKeys.detail(id),
    queryFn: ({ signal }) => api.get<RoomDetail>(`/rooms/${id}`, { signal }),
  })

export const useRoom = (id: number) => useQuery(roomQuery(id))

/** Room transcript (newest page; older pages are prepended by {@link loadEarlierMessages}). */
export const roomMessagesQuery = (id: number) =>
  queryOptions({
    queryKey: roomKeys.messages(id),
    queryFn: ({ signal }) =>
      api.get<ListResponse<RoomMessage>>(`/rooms/${id}/messages`, { signal, query: { limit: 200 } }),
  })

export const useRoomMessages = (id: number) => useQuery(roomMessagesQuery(id))

/** Fetches the page before the oldest loaded message and prepends it to the cache. */
export async function loadEarlierMessages(qc: QueryClient, id: number) {
  const cur = qc.getQueryData<ListResponse<RoomMessage>>(roomKeys.messages(id))
  const oldest = cur?.items[0]?.id
  if (!cur || oldest == null) return
  const page = await api.get<ListResponse<RoomMessage>>(`/rooms/${id}/messages`, {
    query: { before_id: oldest, limit: 200 },
  })
  qc.setQueryData<ListResponse<RoomMessage>>(roomKeys.messages(id), (old) => {
    if (!old) return old
    const seen = new Set(old.items.map((m) => m.id))
    return {
      ...old,
      total: page.total ?? old.total,
      items: [...page.items.filter((m) => !seen.has(m.id)), ...old.items],
    }
  })
}

/** Writes a room into its detail entry and every cached list. */
export function patchRoomCaches(qc: QueryClient, room: RoomDetail) {
  qc.setQueryData(roomKeys.detail(room.id), room)
  qc.setQueriesData<ListResponse<RoomSummary>>({ queryKey: roomKeys.lists() }, (old) =>
    old
      ? { ...old, items: old.items.map((r) => (r.id === room.id ? { ...r, ...summaryOf(room) } : r)) }
      : old,
  )
}

function summaryOf(d: RoomDetail): RoomSummary {
  return {
    id: d.id,
    title: d.title,
    team_id: d.team_id,
    team_name: d.team_name,
    project_id: d.project_id,
    owner_id: d.owner_id,
    status: d.status,
    paused: d.paused,
    run_phase: d.run_phase,
    run_phase_updated_at: d.run_phase_updated_at,
    orchestrator_status: d.orchestrator_status,
    turn_count: d.turn_count,
    max_turns_budget: d.max_turns_budget,
    generation: d.generation,
    next_in_line_is_user: d.next_in_line_is_user,
    human_in_the_loop_enabled: d.human_in_the_loop_enabled,
    context_id: d.context_id,
    created_at: d.created_at,
    updated_at: d.updated_at,
  }
}

export interface RoomCreateInput {
  team_id: number
  title?: string
  additional_instructions?: string
  max_turns_budget?: number | null
  human_in_the_loop_enabled?: boolean
}

export function useCreateRoom() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  return useApiMutation<RoomDetail, RoomCreateInput>({
    mutationFn: (body) => api.post("/rooms", body),
    onSuccess: (r) => qc.setQueryData(roomKeys.detail(r.id), r),
    invalidates: [roomKeys.lists(), roomKeys.teamPicker()],
    successToast: () => t("workplace.rooms.created"),
    errorToast: "unlessFields",
  })
}

export interface RoomPatch {
  title?: string
  additional_instructions?: string
  context_max_messages?: number
  context_compression_enabled?: boolean
  context_compression_use_kb?: boolean
  max_turns_budget?: number | null
  max_concurrent_invocations?: number
  orchestrator_pass_budget?: number
  human_in_the_loop_enabled?: boolean
  human_in_the_loop_condition?: string
  sentry_organization_slug?: string
  guardrails?: string[]
  room_data?: Record<string, string>
}

export function useUpdateRoom() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  return useApiMutation<RoomDetail, { id: number; silent?: boolean } & RoomPatch>({
    mutationFn: ({ id, silent: _s, ...body }) => api.patch(`/rooms/${id}`, body),
    onSuccess: (r) => patchRoomCaches(qc, r),
    successToast: (_r, v) => !v.silent && t("workplace.room.settings.saved"),
    errorToast: "unlessFields",
  })
}

/** Sets one room data key server-side (optionally renaming `renameFrom`), leaving other keys untouched. */
export function usePutRoomData(roomId: number) {
  const qc = useQueryClient()
  return useApiMutation<RoomDetail, { key: string; value: string; renameFrom?: string }>({
    mutationFn: ({ key, value, renameFrom }) =>
      api.put(`/rooms/${roomId}/data/${encodeURIComponent(key)}`, { value, rename_from: renameFrom ?? null }),
    onSuccess: (r) => patchRoomCaches(qc, r),
    errorToast: "unlessFields",
  })
}

export function useDeleteRoomData(roomId: number) {
  const qc = useQueryClient()
  return useApiMutation<RoomDetail, string>({
    mutationFn: (key) => api.delete(`/rooms/${roomId}/data/${encodeURIComponent(key)}`),
    onSuccess: (r) => patchRoomCaches(qc, r),
  })
}

/** Drops a deleted room's caches (call once its page is no longer mounted). */
export function forgetRoom(qc: QueryClient, id: number) {
  for (const key of [roomKeys.detail(id), roomKeys.messages(id), roomKeys.files(id)])
    qc.removeQueries({ queryKey: key })
}

export function useDeleteRoom() {
  const { t } = useTranslation()
  return useApiMutation<void, Pick<RoomSummary, "id" | "title">>({
    mutationFn: (r) => api.delete(`/rooms/${r.id}`),
    invalidates: [roomKeys.lists(), roomKeys.teamPicker()],
    successToast: () => t("workplace.rooms.deleted"),
  })
}

export function useDuplicateRoom() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  return useApiMutation<RoomDetail, { id: number; title?: string }>({
    mutationFn: ({ id, title }) => api.post(`/rooms/${id}/duplicate`, { title }),
    onSuccess: (r) => qc.setQueryData(roomKeys.detail(r.id), r),
    invalidates: [roomKeys.lists()],
    successToast: (r) => t("workplace.rooms.duplicated", { title: r.title }),
  })
}

// ------------------------------------------------------------------ control

export type RoomControl = "pause" | "resume" | "stop"

/** Pause / resume / stop: all return the updated room. */
export function useRoomControl(id: number) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  return useApiMutation<RoomDetail, RoomControl>({
    mutationFn: (action) => api.post(`/rooms/${id}/${action}`),
    onSuccess: (r) => patchRoomCaches(qc, r),
    successToast: (_r, action) => (action === "stop" ? t("workplace.room.control.stopped") : false),
  })
}

/** Pause / resume any room (list row actions). */
export function useToggleRoomPause() {
  const qc = useQueryClient()
  return useApiMutation<RoomDetail, Pick<RoomSummary, "id" | "paused">>({
    mutationFn: (r) => api.post(`/rooms/${r.id}/${r.paused ? "resume" : "pause"}`),
    onSuccess: (r) => patchRoomCaches(qc, r),
  })
}

export function useStepRoom(id: number) {
  const { t } = useTranslation()
  return useApiMutation<StepAccepted, void>({
    mutationFn: () => api.post(`/rooms/${id}/step`),
    successToast: () => t("workplace.room.control.stepRequested"),
  })
}

export function useCancelRoomTurns(id: number) {
  const { t } = useTranslation()
  return useApiMutation<CancelResult, void>({
    mutationFn: () => api.post(`/rooms/${id}/cancel`),
    invalidates: [roomKeys.detail(id)],
    successToast: (r) => t("workplace.room.control.cancelled", { count: r.released_workers }),
  })
}

export function useCompressRoom(id: number) {
  const { t } = useTranslation()
  return useApiMutation<{ compressed: boolean }, void>({
    mutationFn: () => api.post(`/rooms/${id}/compress`),
    invalidates: [roomKeys.detail(id)],
    successToast: (r) =>
      r.compressed ? t("workplace.room.memory.compressed") : t("workplace.room.memory.nothingToCompress"),
  })
}

// ------------------------------------------------------------------ messages

export function usePostRoomMessage(id: number) {
  const qc = useQueryClient()
  return useApiMutation<RoomMessage, { content: string; attachment_ids: number[] }>({
    mutationFn: (body) => api.post(`/rooms/${id}/messages`, body),
    onSuccess: (m) => appendMessage(qc, id, m),
  })
}

/** Adds a message to the cached transcript (deduplicated by id, kept in id order). */
export function appendMessage(qc: QueryClient, roomId: number, m: RoomMessage) {
  qc.setQueryData<ListResponse<RoomMessage>>(roomKeys.messages(roomId), (old) => {
    if (!old) return old
    if (old.items.some((x) => x.id === m.id)) return old
    const items = [...old.items, m].sort((a, b) => a.id - b.id)
    return { ...old, items, total: (old.total ?? old.items.length) + 1 }
  })
}

/**
 * Uploads a room attachment with progress. The result is shaped like a chat
 * upload so the chat composer's attachment queue can be reused.
 */
export function uploadRoomAttachment(
  roomId: number,
  file: File,
  opts: { onProgress?: (fraction: number) => void; signal?: AbortSignal } = {},
) {
  return xhrUpload<RoomAttachment>(`/rooms/${roomId}/attachments`, file, opts).then((a) => ({
    ...a,
    created_at: new Date().toISOString(),
  }))
}

function xhrUpload<T>(
  path: string,
  file: File,
  opts: { onProgress?: (fraction: number) => void; signal?: AbortSignal },
): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open("POST", buildUrl(path))
    xhr.withCredentials = true
    for (const [k, v] of Object.entries(baseHeaders())) xhr.setRequestHeader(k, v)
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) opts.onProgress?.(e.loaded / e.total)
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as T)
        } catch {
          reject(new ApiError(xhr.status, "invalid_json", "The server returned an invalid response."))
        }
        return
      }
      void toApiError(new Response(xhr.responseText || null, { status: xhr.status }), path).then(reject)
    }
    xhr.onerror = () => reject(new ApiError(0, "network_error", "Network error"))
    xhr.onabort = () => reject(new DOMException("Upload aborted", "AbortError"))
    opts.signal?.addEventListener("abort", () => xhr.abort(), { once: true })
    const body = new FormData()
    body.append("file", file, file.name)
    xhr.send(body)
  })
}

// ------------------------------------------------------------------ context bullets

export function useAddBullet(roomId: number) {
  const { t } = useTranslation()
  return useApiMutation<RoomContextBullet, { text: string }>({
    mutationFn: (body) => api.post(`/rooms/${roomId}/context`, { ...body, category: "task_status" }),
    invalidates: [roomKeys.detail(roomId)],
    successToast: () => t("workplace.room.context.added"),
  })
}

export function useUpdateBullet(roomId: number) {
  return useApiMutation<RoomContextBullet, { id: number; text: string }>({
    mutationFn: ({ id, text }) => api.patch(`/rooms/${roomId}/context/${id}`, { text }),
    invalidates: [roomKeys.detail(roomId)],
  })
}

export function useDeleteBullet(roomId: number) {
  const { t } = useTranslation()
  return useApiMutation<void, number>({
    mutationFn: (id) => api.delete(`/rooms/${roomId}/context/${id}`),
    invalidates: [roomKeys.detail(roomId)],
    successToast: () => t("workplace.room.context.deleted"),
  })
}

// ------------------------------------------------------------------ workers

export function useDespawnWorker(roomId: number) {
  const { t } = useTranslation()
  return useApiMutation<RoomWorker, RoomWorker>({
    mutationFn: (w) => api.post(`/rooms/${roomId}/workers/${w.id}/despawn`),
    invalidates: [roomKeys.detail(roomId)],
    successToast: (w) => t("workplace.room.workers.despawned", { name: w.display_name }),
  })
}

// ------------------------------------------------------------------ files

export const roomFilesQuery = (roomId: number) =>
  queryOptions({
    queryKey: roomKeys.files(roomId),
    queryFn: ({ signal }) => api.get<ListResponse<RoomFile>>(`/rooms/${roomId}/files`, { signal }),
    select: (r) => r.items,
  })

export const useRoomFiles = (roomId: number, enabled = true) =>
  useQuery({ ...roomFilesQuery(roomId), enabled })

/** File with its extracted text (text files: the content itself). */
export const useRoomFile = (roomId: number, fileId: number | undefined) =>
  useQuery({
    queryKey: roomKeys.file(roomId, fileId ?? 0),
    queryFn: ({ signal }) => api.get<RoomFile>(`/rooms/${roomId}/files/${fileId}`, { signal }),
    enabled: fileId != null,
  })

export function useUploadRoomFile(roomId: number) {
  const { t } = useTranslation()
  return useApiMutation<RoomFile, File>({
    mutationFn: (file) => xhrUpload<RoomFile>(`/rooms/${roomId}/files`, file, {}),
    invalidates: [roomKeys.files(roomId)],
    successToast: (f) => t("workplace.room.files.uploaded", { name: f.original_filename }),
  })
}

export function useSaveRoomTextFile(roomId: number) {
  const { t } = useTranslation()
  return useApiMutation<RoomFile, { id?: number; filename: string; content: string }>({
    mutationFn: ({ id, ...body }) =>
      id ? api.patch(`/rooms/${roomId}/files/${id}`, body) : api.post(`/rooms/${roomId}/files`, body),
    invalidates: (f) => [roomKeys.files(roomId), roomKeys.file(roomId, f.id)],
    successToast: (f) => t("workplace.room.files.saved", { name: f.original_filename }),
    errorToast: "unlessFields",
  })
}

export function useDeleteRoomFile(roomId: number) {
  const { t } = useTranslation()
  return useApiMutation<void, RoomFile>({
    mutationFn: (f) => api.delete(`/rooms/${roomId}/files/${f.id}`),
    invalidates: [roomKeys.files(roomId)],
    successToast: (_d, f) => t("workplace.room.files.deleted", { name: f.original_filename }),
  })
}

/** Saves an authenticated download with the tab's tenant header (a plain link could hit another organization). */
async function saveDownload(path: string, filename: string) {
  const res = await fetch(buildUrl(path), { headers: baseHeaders(), credentials: "same-origin" })
  if (!res.ok) throw await toApiError(res, path)
  const url = URL.createObjectURL(await res.blob())
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  a.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export const downloadRoomFile = (roomId: number, file: Pick<RoomFile, "id" | "original_filename">) =>
  saveDownload(`/rooms/${roomId}/files/${file.id}/download`, file.original_filename)

/** Downloads a file attached to a room message. */
export const downloadRoomAttachment = (roomId: number, a: Pick<RoomAttachment, "id" | "original_name">) =>
  saveDownload(`/rooms/${roomId}/attachments/${a.id}`, a.original_name)

// ------------------------------------------------------------------ search

export const useRoomSearch = (roomId: number, q: string, scope: SearchScope) =>
  useQuery({
    queryKey: roomKeys.search(roomId, q.trim(), scope),
    queryFn: ({ signal }) =>
      api.get<RoomSearchResult>(`/rooms/${roomId}/search`, { signal, query: { q: q.trim(), scope } }),
    enabled: q.trim().length > 0,
    placeholderData: keepPreviousData,
  })

// ------------------------------------------------------------------ teams (picker only)

export const roomTeamPickerQuery = () =>
  queryOptions({
    queryKey: roomKeys.teamPicker(),
    queryFn: ({ signal }) => api.get<ListResponse<Team>>("/teams", { signal }),
    select: (r) => r.items,
    staleTime: 30_000,
  })

export const useRoomTeamPicker = (enabled = true) => useQuery({ ...roomTeamPickerQuery(), enabled })
